import React, { useCallback, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { getInputStyles, getSecondaryText } from "../utils/themeUtils";
import ConfirmDeleteModal from "../components/common/ConfirmDeleteModal";

/**
 * Promise-based confirm hook — the drop-in replacement for
 * `window.confirm` in the 2026-06-12 confirm-dialog sweep.
 * window.confirm is unlabelled for screen readers, silently blocked in
 * iframes, and unstyleable; this opens the shared ConfirmDeleteModal
 * and resolves the Promise with the user's choice.
 *
 * Usage:
 *   const { confirm, confirmModal } = useConfirmModal();
 *
 *   const handleDelete = async () => {
 *     if (!(await confirm({ message: "Delete this resource?", confirmText: "Delete" }))) return;
 *     ...destructive action...
 *   };
 *
 *   return (
 *     <>
 *       {confirmModal}
 *       ...
 *     </>
 *   );
 *
 * `confirmWithInput` replaces the window.confirm + window.prompt pair
 * (e.g. "cancel + optional reason") with a single modal. Resolves the
 * entered string (possibly "") on confirm, or null on cancel:
 *
 *   const reason = await confirmWithInput({
 *     title: "Cancel booking?",
 *     message: "...",
 *     input: { label: "Reason (optional)" },
 *   });
 *   if (reason === null) return;
 */

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Forces the confirm button style; defaults to ConfirmDeleteModal's confirmText heuristic */
  variant?: "primary" | "danger";
}

export interface ConfirmWithInputOptions extends ConfirmOptions {
  input?: {
    label?: string;
    placeholder?: string;
    /** Pre-fills the field (e.g. editing an existing value) */
    defaultValue?: string;
    /** Confirm stays disabled until the field has a non-empty value */
    required?: boolean;
    /** Single-line <input> instead of the default multi-line <textarea> */
    multiline?: boolean;
    /** input type for the single-line variant (e.g. "time", "number") */
    type?: string;
  };
}

export function useConfirmModal() {
  const { isDarkMode } = useTheme();
  const [options, setOptions] = useState<ConfirmWithInputOptions | null>(null);
  const [inputValue, setInputValue] = useState("");
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const inputResolverRef = useRef<((value: string | null) => void) | null>(null);

  const cancelPending = useCallback(() => {
    // If a previous prompt is still open, resolve it as cancelled
    // before opening the new one.
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    if (inputResolverRef.current) {
      inputResolverRef.current(null);
      inputResolverRef.current = null;
    }
  }, []);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    cancelPending();
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, [cancelPending]);

  const confirmWithInput = useCallback((opts: ConfirmWithInputOptions): Promise<string | null> => {
    cancelPending();
    setInputValue(opts.input?.defaultValue ?? "");
    setOptions({ ...opts, input: opts.input || {} });
    return new Promise<string | null>((resolve) => {
      inputResolverRef.current = resolve;
    });
  }, [cancelPending]);

  const settle = useCallback((confirmed: boolean, value: string) => {
    setOptions(null);
    if (resolverRef.current) {
      resolverRef.current(confirmed);
      resolverRef.current = null;
    }
    if (inputResolverRef.current) {
      inputResolverRef.current(confirmed ? value : null);
      inputResolverRef.current = null;
    }
  }, []);

  const confirmModal = options ? (
    <ConfirmDeleteModal
      isOpen
      title={options.title || "Are you sure?"}
      message={options.message}
      confirmText={options.confirmText || "Confirm"}
      cancelText={options.cancelText || "Cancel"}
      confirmVariant={options.variant}
      confirmDisabled={!!options.input?.required && inputValue.trim() === ""}
      onCancel={() => settle(false, inputValue)}
      onConfirm={() => settle(true, inputValue)}
    >
      {options.input && (
        <div className="mb-6">
          {options.input.label && (
            <label className={`block text-sm mb-1 ${getSecondaryText(isDarkMode)}`}>
              {options.input.label}
            </label>
          )}
          {options.input.multiline === false ? (
            <input
              aria-label={options.input.label || "Response"}
              type={options.input.type || "text"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={options.input.placeholder}
              autoFocus
              className={`w-full p-2 rounded ${getInputStyles(isDarkMode)}`}
            />
          ) : (
            <textarea
              aria-label={options.input.label || "Response"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={options.input.placeholder}
              rows={2}
              autoFocus
              className={`w-full p-2 rounded ${getInputStyles(isDarkMode)}`}
            />
          )}
        </div>
      )}
    </ConfirmDeleteModal>
  ) : null;

  return { confirm, confirmWithInput, confirmModal };
}
